import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// =============================================================================
// READMISSION RISK ENDPOINTS
// =============================================================================

/**
 * Get readmission risk for a specific patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Patient readmission risk data
 */
export async function getPatientReadmissionRisk(patientId, facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/patient/${patientId}/readmission-risk`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting patient readmission risk:', error);
    throw error;
  }
}

/**
 * Get facility-wide readmission risk summary
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Summary of readmission risks by tier
 */
export async function getReadmissionRiskSummary(facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/readmission-risk/summary`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting readmission risk summary:', error);
    throw error;
  }
}

/**
 * Get list of high-risk patients for readmission
 * @param {string} facilityId - Facility ID
 * @param {number} limit - Maximum number of patients to return
 * @returns {Promise<Object>} List of high-risk patients
 */
export async function getHighRiskReadmissionPatients(facilityId, limit = 50) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/readmission-risk/high-risk-patients`,
      {
        params: { facilityId, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting high-risk readmission patients:', error);
    throw error;
  }
}

// =============================================================================
// NO-SHOW PREDICTION ENDPOINTS
// =============================================================================

/**
 * Get no-show predictions for upcoming appointments
 * @param {string} facilityId - Facility ID
 * @param {number} days - Number of days ahead to predict (default 7)
 * @returns {Promise<Object>} List of appointments with no-show predictions
 */
export async function getNoShowPredictions(facilityId, days = 7) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/no-show/upcoming`,
      {
        params: { facilityId, days },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting no-show predictions:', error);
    throw error;
  }
}

/**
 * Get daily no-show summary for the next two weeks
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Daily summary of expected no-shows
 */
export async function getNoShowDailySummary(facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/no-show/daily-summary`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting no-show daily summary:', error);
    throw error;
  }
}

// =============================================================================
// LENGTH OF STAY PREDICTION ENDPOINTS
// =============================================================================

/**
 * Get length of stay prediction for an encounter
 * @param {string} encounterId - Encounter ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} LOS prediction data
 */
export async function getLOSPrediction(encounterId, facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/los/${encounterId}`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting LOS prediction:', error);
    throw error;
  }
}

/**
 * Get bed capacity forecast based on LOS predictions
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Bed capacity forecast data
 */
export async function getBedCapacityForecast(facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/los/bed-capacity-forecast`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting bed capacity forecast:', error);
    throw error;
  }
}

// =============================================================================
// POPULATION HEALTH / RISK STRATIFICATION ENDPOINTS
// =============================================================================

/**
 * Get population health summary with risk stratification
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Population health summary by risk tier
 */
export async function getPopulationHealthSummary(facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/population-health/summary`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting population health summary:', error);
    throw error;
  }
}

/**
 * Get high-risk patients for care management
 * @param {string} facilityId - Facility ID
 * @param {number} limit - Maximum number of patients to return
 * @returns {Promise<Object>} List of high-risk patients
 */
export async function getHighRiskPatients(facilityId, limit = 100) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/population-health/high-risk-patients`,
      {
        params: { facilityId, limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting high-risk patients:', error);
    throw error;
  }
}

/**
 * Get comprehensive risk profile for a specific patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Comprehensive patient risk profile
 */
export async function getPatientRiskProfile(patientId, facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/patient/${patientId}/risk-profile`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting patient risk profile:', error);
    throw error;
  }
}

// =============================================================================
// MODEL PERFORMANCE ENDPOINTS
// =============================================================================

/**
 * Get model performance metrics (admin only)
 * @returns {Promise<Object>} Model performance metrics
 */
export async function getModelPerformance() {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/model-performance`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting model performance:', error);
    throw error;
  }
}

/**
 * Check predictive analytics service health
 * @returns {Promise<Object>} Service health status
 */
export async function getPredictiveAnalyticsHealth() {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/predictive/health`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error checking predictive analytics health:', error);
    throw error;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get risk tier color for UI display
 * @param {string} tier - Risk tier (HIGH, MEDIUM, LOW, MINIMAL, VERY_HIGH, MODERATE)
 * @returns {string} Color code for the tier
 */
export function getRiskTierColor(tier) {
  const colors = {
    // Uppercase format
    'VERY_HIGH': '#d32f2f',  // Dark red
    'HIGH': '#f44336',       // Red
    'MEDIUM': '#ff9800',     // Orange
    'MODERATE': '#ff9800',   // Orange
    'LOW': '#4caf50',        // Green
    'MINIMAL': '#8bc34a',    // Light green
    // Lowercase underscore format (from BigQuery ML)
    'high_risk': '#f44336',       // Red
    'rising_risk': '#ff9800',     // Orange  
    'moderate_risk': '#ff9800',   // Orange
    'low_risk': '#4caf50',        // Green
  };
  return colors[tier] || '#9e9e9e';  // Grey for unknown
}

/**
 * Get risk tier label for UI display
 * @param {string} tier - Risk tier
 * @returns {string} Human-readable label
 */
export function getRiskTierLabel(tier) {
  const labels = {
    // Uppercase format
    'VERY_HIGH': 'Very High Risk',
    'HIGH': 'High Risk',
    'MEDIUM': 'Medium Risk',
    'MODERATE': 'Moderate Risk',
    'LOW': 'Low Risk',
    'MINIMAL': 'Minimal Risk',
    // Lowercase underscore format (from BigQuery ML)
    'high_risk': 'High Risk',
    'rising_risk': 'Rising Risk',
    'moderate_risk': 'Moderate Risk',
    'low_risk': 'Low Risk',
  };
  return labels[tier] || tier || 'Unknown';
}

/**
 * Format probability as percentage string
 * @param {number} probability - Probability value (0-1)
 * @returns {string} Formatted percentage
 */
export function formatProbabilityPercent(probability) {
  if (probability === null || probability === undefined) {
    return 'N/A';
  }
  return `${Math.round(probability * 100)}%`;
}

/**
 * Get recommended action text for no-show risk
 * @param {string} action - Action code
 * @returns {string} Human-readable action
 */
export function getNoShowActionText(action) {
  const actions = {
    'CALL_CONFIRM_24HR': 'Call to confirm 24 hours before',
    'SMS_REMINDER': 'Send SMS reminder',
    'STANDARD_REMINDER': 'Standard reminder only',
  };
  return actions[action] || action;
}

/**
 * Get intervention text for risk level
 * @param {string} intervention - Intervention code
 * @returns {Object} Intervention details with icon and description
 */
export function getInterventionDetails(intervention) {
  const interventions = {
    'Care Management Enrollment': {
      icon: '🏥',
      description: 'Enroll in intensive care management program',
      priority: 1
    },
    'Care Coordination Outreach': {
      icon: '📞',
      description: 'Schedule care coordination call',
      priority: 2
    },
    'Preventive Care Scheduling': {
      icon: '📅',
      description: 'Schedule preventive care visit',
      priority: 3
    },
    'Standard Care': {
      icon: '✓',
      description: 'Continue standard care protocol',
      priority: 4
    }
  };
  return interventions[intervention] || { icon: '?', description: intervention, priority: 5 };
}
